import { useState } from 'react';
import { PeopleField, parseEmailList } from './PeopleField';
import { Input } from '@finn-auto/design-system/atoms/input';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { FeedbackFormSelect } from '../interviewLinks/FeedbackFormSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import type { PanelConfig, RoleBoard } from '../../types/taActionBoard';
import { ModalActions } from './ModalActions';
import type { PanelSuggestion } from './PanelSuggestion';

/** Interview lengths offered — 15-minute steps, matching the proxy's grid. */
const MINUTE_OPTIONS = Array.from({ length: 15 }, (_, i) => 30 + i * 15); // 30 … 240

const isFinn = (e: string) => e.endsWith('@finn.com');

/**
 * Edit a role's case panel.
 *
 * ONE pool of people who can run this role's case, and the board picks the
 * actual seats PER CANDIDATE: whoever they have NOT met comes first, because a
 * case is a fresh assessment and more different interviewers mean more
 * perspectives behind the decision. A person marked FIXED is the exception —
 * they hold a seat whatever the candidate has already seen.
 *
 * A kick-off-sheet suggestion pre-fills the pool when the role has no panel
 * yet; nothing is stored until the recruiter saves.
 */
export function EditPanelBody({
  role,
  suggestion = null,
  onConfirm,
  onCancel,
}: {
  role: RoleBoard;
  suggestion?: PanelSuggestion | null;
  onConfirm: (panel: PanelConfig) => void;
  onCancel: () => void;
}) {
  const panel = role.scheduling.panel;
  // an existing panel always wins over the suggestion — this modal also serves plain edits
  const configured = (panel?.pool ?? panel?.interviewers ?? []).length > 0;
  const seeded = !configured && suggestion ? suggestion : null;

  /**
   * ONE pool now. A panel saved under the old two-field shape arrives with
   * `pool` already merged by the proxy's normalizer, so this only needs the
   * legacy `interviewers` fallback for a board payload cached before that.
   */
  const [pool, setPool] = useState(
    (panel?.pool ?? panel?.interviewers ?? seeded?.interviewers ?? []).join(', ')
  );
  /**
   * Who is pinned to every panel for this role, whatever the candidate has
   * already seen — the department lead who ran the personal interview and
   * wants the case too. Everyone else is seated to maximise NEW faces.
   */
  const [fixed, setFixed] = useState<string[]>(panel?.fixed ?? []);
  const [interviewerCount, setInterviewerCount] = useState(String(panel?.interviewerCount ?? 2));
  const [minutes, setMinutes] = useState(String(panel?.minutes || seeded?.minutes || 90));
  // The Lever scorecard the panel fills in afterwards. Confirming a case attaches
  // it to the Lever interview, the same way an interview link does for the pre
  // and personal rounds — without one the round has no form waiting for it.
  const [feedbackTemplate, setFeedbackTemplate] = useState(panel?.feedbackTemplate ?? '');
  /**
   * The case material, as a shared LINK.
   *
   * It lives here rather than as its own row on the board so there is one place
   * to configure a case. Uploading a file was the original design and has never
   * worked in production — the deployed Firebase Storage rules carry no
   * `people_space/` block and this repo cannot deploy them — so the field is a
   * URL, which every FINN case has anyway.
   */
  const stored = panel?.caseMaterial ?? null;
  const [materialUrl, setMaterialUrl] = useState(stored?.kind === 'link' ? stored.url : '');
  const [materialLabel, setMaterialLabel] = useState(stored?.kind === 'link' ? stored.label : '');
  const materialUrlOk = materialUrl.trim() === '' || /^https:\/\/\S+$/i.test(materialUrl.trim());

  // The FIELD's own parser, not a second copy: this one had drifted and kept
  // duplicates, so "a@finn.com, a@finn.com" drew one chip while the seat count
  // saw two people and swallowed the short-panel warning.
  const poolList = parseEmailList(pool);
  const valid = poolList.length > 0 && poolList.every(isFinn) && Number(minutes) > 0 && materialUrlOk;

  // A name removed from the pool must not stay pinned behind the scenes.
  const pinned = fixed.filter((e) => poolList.includes(e));
  const short = poolList.length > 0 && poolList.length < Number(interviewerCount);
  /**
   * No seat can ever go to a fresh face, AND someone in the pool is being shut
   * out by that. Both halves matter: the second clause used to compare against
   * `pinned.length`, which stayed quiet when MORE people were pinned than there
   * are seats (three pinned for two seats — the third is never seated, and the
   * candidate meets the same two every time). Dropping it altogether is the
   * other error: a pool of exactly two for two seats seats everyone whatever is
   * pinned, so "unpin someone" would be advice that changes nothing, on what is
   * the ordinary shape of a small role.
   */
  const allFixed =
    pinned.length >= Number(interviewerCount) && poolList.length > Number(interviewerCount);

  return (
    <>
      <ModalHeader>
        <ModalTitle>Edit panel — {role.roleTitle}</ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        {seeded && (
          <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
            Pre-filled from the role's kick-off sheet — review the names and duration before
            saving.
          </p>
        )}
        <PeopleField
          id="case-panel-pool"
          label="Interviewers for this role"
          value={pool}
          onChange={setPool}
          pinned={pinned}
          onTogglePin={(email) =>
            setFixed((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
          }
          pinLabel="fixed panelist"
          pinTitle="Always on the panel for this role, whatever the candidate has already seen"
          hint="Seats go to whoever the candidate has NOT met yet — a case is a fresh assessment, so the more different interviewers they meet, the more perspectives the decision rests on. Mark someone “fixed panelist” to put them on every panel anyway: the department lead who ran the personal interview and wants the case too."
        />
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[12rem] flex-1">
            <Select value={interviewerCount} onValueChange={setInterviewerCount}>
              <SelectTrigger label="Interviewers needed" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 interviewer</SelectItem>
                <SelectItem value="2">2 interviewers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[12rem] flex-1">
            <Select value={minutes} onValueChange={setMinutes}>
              <SelectTrigger label="Duration" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MINUTE_OPTIONS.map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {m} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-800">Feedback form</label>
          <p className="mb-1 text-xs text-gray-500">
            Attached to the Lever interview when you confirm a case, so the panel finds the right
            scorecard waiting for them.
          </p>
          {/* No stage id: the per-stage suggestion needs one, and a case panel is
              configured per ROLE. It degrades to the plain template list. */}
          <FeedbackFormSelect
            value={feedbackTemplate}
            postingId={role.postingId}
            stageId=""
            onChange={setFeedbackTemplate}
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-gray-800">Case material</label>
          <p className="text-xs text-gray-500">
            A link the candidate can open — it goes out in the slot proposal for this role. Drive,
            Notion and Confluence all work; check the sharing settings before you save. Leave it
            empty to send nothing.
          </p>
          <Input
            label="Link"
            value={materialUrl}
            onChange={(e) => setMaterialUrl((e.target as HTMLInputElement).value)}
            placeholder="https://drive.google.com/…"
          />
          {materialUrl.trim() !== '' && (
            <Input
              label="What to call it (optional)"
              value={materialLabel}
              onChange={(e) => setMaterialLabel((e.target as HTMLInputElement).value)}
              placeholder="Case material"
            />
          )}
          {!materialUrlOk && (
            <p className="text-xs text-[#854F0B]">
              That needs to be an https link, or empty.
            </p>
          )}
        </div>
        {short && (
          <p className="rounded-md bg-[#FCEEE2] px-3 py-2 text-xs text-[#854F0B]">
            {poolList.length} {poolList.length === 1 ? 'person' : 'people'} in the pool, but{' '}
            {interviewerCount} interviewers are needed — some panels will come up a seat short.
          </p>
        )}
        {allFixed && (
          <p className="rounded-md bg-[#FCEEE2] px-3 py-2 text-xs text-[#854F0B]">
            {pinned.length} fixed {pinned.length === 1 ? 'panelist' : 'panelists'} for{' '}
            {interviewerCount} {Number(interviewerCount) === 1 ? 'seat' : 'seats'} — every candidate
            meets the same people, and nobody new. Raise the seat count, or unpin someone.
          </p>
        )}
      </div>
      {/*
        A disabled button with no reason is a dead end — this one sent a
        recruiter guessing at an interviewer-count rule that does not exist
        (Theresa, Sep 2026). The count never blocks saving; only these do.
      */}
      {!valid && (
        <p className="text-xs text-gray-500">
          {poolList.length === 0
            ? 'Add at least one interviewer to save.'
            : !Number(minutes)
              ? 'Pick how long the interview runs to save.'
              : `Not a @finn.com address: ${poolList.filter((e) => !isFinn(e)).join(', ')}`}
        </p>
      )}
      <ModalActions
        confirmLabel="Save panel"
        confirmDisabled={!valid}
        onConfirm={() =>
          onConfirm({
            pool: poolList,
            fixed: pinned,
            interviewerCount: Number(interviewerCount),
            // Role-level seating for cached readers; the server re-resolves it
            // per candidate (resolveCandidatePanel).
            interviewers: poolList.slice(0, Number(interviewerCount)),
            fallback: poolList[Number(interviewerCount)] || null,
            minutes: Number(minutes),
            note: panel?.note || null,
            feedbackTemplate: feedbackTemplate || null,
            /**
             * Saved with the panel: one write, so there is no state where the
             * panel moved and the material did not.
             *
             * OMITTED entirely when the role still holds file-shaped material
             * and no link has been typed. This field only edits links, so an
             * empty box there means "not editing", not "remove" — sending null
             * would delete a stored file because somebody changed the duration.
             * The server leaves a field it was not sent alone.
             */
            ...(materialUrl.trim()
              ? {
                  caseMaterial: {
                    kind: 'link' as const,
                    url: materialUrl.trim(),
                    label: materialLabel.trim() || 'Case material',
                  },
                }
              : stored && stored.kind !== 'link'
                ? {}
                : { caseMaterial: null }),
          })
        }
        onCancel={onCancel}
      />
    </>
  );
}
